import { computed, Injectable, signal } from '@angular/core';
import { pascalToCamelCaseRecursive } from 'shared/util/pascal-to-camel-case';
import {
    ListingValidationResult,
    ValidationIssue,
    VisualsValidationItem,
    VisualsValidationResponse,
} from '../interfaces/listing-validation';

const EMPTY_VALIDATION_ISSUE: ValidationIssue = { errors: [], warnings: [] };

@Injectable({ providedIn: 'root' })
export class ListingValidationService {
    private _validationResultSignal = signal<ListingValidationResult | null>(null);

    readonly validationResult = this._validationResultSignal.asReadonly();

    readonly content = computed<ValidationIssue>(() => this._validationResultSignal()?.content ?? EMPTY_VALIDATION_ISSUE);
    readonly visuals = computed<ValidationIssue>(() => this._validationResultSignal()?.visuals ?? EMPTY_VALIDATION_ISSUE);

    readonly contentErrors = computed(() => this.content().errors);
    readonly contentWarnings = computed(() => this.content().warnings);
    readonly visualErrors = computed(() => this.visuals().errors);
    readonly visualWarnings = computed(() => this.visuals().warnings);

    readonly hasContentErrors = computed(() => this.contentErrors().length > 0);
    readonly hasVisualErrors = computed(() => this.visualErrors().length > 0);

    readonly errors = computed(() => [...this.contentErrors(), ...this.visualErrors()]);
    readonly warnings = computed(() => [...this.contentWarnings(), ...this.visualWarnings()]);
    readonly hasErrors = computed(() => this.errors().length > 0);

    currentlyLoadedListingId = signal<string | null>(null);

    setValidationResult(result: ListingValidationResult): void {
        this._validationResultSignal.set(result);
    }

    clearValidationResult(): void {
        this._validationResultSignal.set(null);
    }

    mapVisualsValidationResponse(raw: unknown): ValidationIssue {
        const response = raw as VisualsValidationResponse | null | undefined;
        const toMessages = (items?: VisualsValidationItem[]): string[] => items?.map((item) => item.message).filter(Boolean) ?? [];

        return {
            errors: toMessages(response?.errors),
            warnings: toMessages(response?.warnings),
        };
    }

    mapContentValidationResponse(raw: unknown): ValidationIssue {
        const onlyStrings = (value: unknown): string[] =>
            Array.isArray(value) ? (value as unknown[]).filter((message): message is string => typeof message === 'string') : [];

        let errors: string[] = [];
        let warnings: string[] = [];

        if (Array.isArray(raw)) {
            errors = onlyStrings(raw);
        } else if (raw && typeof raw === 'object') {
            const obj = raw as { errors?: unknown; warnings?: unknown };
            errors = onlyStrings(obj.errors);
            warnings = onlyStrings(obj.warnings);
        }

        return { errors, warnings };
    }

    mapPascalCaseToCamelCase(raw: unknown): ListingValidationResult {
        const camel = pascalToCamelCaseRecursive(raw) as {
            listingId?: string;
            contentErrorsAndWarnings?: ValidationIssue;
            visualErrorsAndWarnings?: ValidationIssue;
            isValid?: boolean;
        } | null;

        return {
            listingId: camel?.listingId ?? '',
            content: camel?.contentErrorsAndWarnings ?? EMPTY_VALIDATION_ISSUE,
            visuals: camel?.visualErrorsAndWarnings ?? EMPTY_VALIDATION_ISSUE,
            isValid: camel?.isValid ?? false,
        };
    }
}
